import { useEffect, useMemo, useRef, useState } from "react";
import {
  getCurrentUser,
  fetchUserAttributes,
  updateUserAttributes,
  fetchAuthSession,
} from "aws-amplify/auth";
import { uploadData, getUrl, remove } from "aws-amplify/storage";
import { Hub } from "aws-amplify/utils";
import { client } from "../../amplify-client";
import {
  PROFILE_IMAGE_OUTPUT_EXTENSION,
  PROFILE_IMAGE_OUTPUT_TYPE,
  clampImageOffsets,
  loadImageDimensionsFromUrl,
  renderCroppedProfileImage,
  revokeObjectUrl,
  validateProfileImageFile,
} from "../../components/profile-image-editor/profile-image-editor.functions";
import {
  evaluateClientTextModeration,
  extractModerationFeedback,
} from "../../api/moderation/content-moderation-service";
import {
  buildProfilePictureStoragePath,
  resolveProfilePictureOwnerId,
} from "../../api/profile/profile-picture-storage-path";

const MINIMUM_PROFILE_AGE = 16;

const DEFAULT_SOCIOPOLITICAL_FILTERS = {
  politicalSpectrum: { value: 4, irrelevant: false },
  immigration: { value: 4, irrelevant: false },
  gunControl: { value: 4, irrelevant: false },
  abortion: { value: 4, irrelevant: false },
  climateChange: { value: 4, irrelevant: false },
  taxPolicy: { value: 4, irrelevant: false },
  lawEnforcement: { value: 4, irrelevant: false },
  votingIntegrity: { value: 4, irrelevant: false },
  regulatoryPhilosophy: { value: 4, irrelevant: false },
  tradePolicy: { value: 4, irrelevant: false },
  healthcare: { value: 4, irrelevant: false },
};

// Builds a unique storage path for a user's processed profile image upload.
export function buildProfilePicturePath(cognitoId, fileExtension, ownerId = cognitoId) {
  return buildProfilePictureStoragePath({
    ownerId,
    userId: cognitoId,
    fileExtension,
  });
}

// Detects duplicate-profile-name backend errors so the UI can show a friendlier message.
export function isProfileNameTakenError(message) {
  const normalizedMessage = String(message || "").toLowerCase();
  return (
    normalizedMessage.includes("profile name already taken") ||
    normalizedMessage.includes("e11000")
  );
}

// Converts the slider-based sociopolitical inputs into persisted numeric answers.
export function normalizeSociopoliticalAnswers(sociopoliticalFilters = {}) {
  return Object.entries(sociopoliticalFilters).reduce((accumulator, [key, question]) => {
    if (question?.irrelevant) {
      accumulator[key] = 0;
      return accumulator;
    }

    const value = Number(question?.value);
    accumulator[key] = Number.isFinite(value) && value >= 1 && value <= 7 ? value : 4;
    return accumulator;
  }, {});
}

// Builds the normalized profile snapshot used to detect unsaved EditProfile changes.
export function buildProfileSnapshot(
  formData = {},
  sociopoliticalFilters = {},
  sociopoliticalPublic = false
) {
  return {
    sociopoliticalPublic: sociopoliticalPublic === true,
    profileName: String(formData.profileName || "").trim(),
    email: String(formData.email || "").trim(),
    country: String(formData.country || "").trim(),
    state: String(formData.state || "").trim(),
    city: String(formData.city || "").trim(),
    dateOfBirth: String(formData.dateOfBirth || ""),
    ethnicity: String(formData.ethnicity || "").trim(),
    maritalStatus: String(formData.maritalStatus || "").trim(),
    occupation: String(formData.occupation || "").trim(),
    religiousAffiliation: String(formData.religiousAffiliation || "").trim(),
    politicalAffiliation: String(formData.politicalAffiliation || "").trim(),
    gender: String(formData.gender || "").trim(),
    sexualOrientation: String(formData.sexualOrientation || "").trim(),
    story: String(formData.story || "").trim(),
    interests: Array.isArray(formData.interests)
      ? [...formData.interests].map((entry) => String(entry || "").trim()).sort()
      : [],
    profilePicture: String(formData.profilePicture || "").trim(),
    sociopoliticalAnswers: normalizeSociopoliticalAnswers(sociopoliticalFilters),
  };
}

export function calculateAge(dateOfBirth, today = new Date()) {
  const birthDate = new Date(dateOfBirth);
  if (Number.isNaN(birthDate.getTime())) return null;

  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1;
  }

  return age;
}

export function validateMinimumProfileAge(dateOfBirth, today = new Date()) {
  const age = calculateAge(dateOfBirth, today);
  if (!Number.isFinite(age)) {
    return "Please enter a valid date of birth.";
  }
  if (age < MINIMUM_PROFILE_AGE) {
    return `You must be at least ${MINIMUM_PROFILE_AGE} years old to use this platform`;
  }
  return null;
}

// Provides EditProfile form state, image handling, validation, and profile persistence logic.
export function useEditProfileForm() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  // Tracks the success-banner auto-dismiss timer so it can be cleared on
  // unmount or before re-arming, preventing a state update after teardown.
  const successTimerRef = useRef(null);
  const savingRef = useRef(false);
  const [uploading, setUploading] = useState(false);
  const [cognitoId, setCognitoId] = useState(null);
  const [email, setEmail] = useState("");
  const [profilePictureUrl, setProfilePictureUrl] = useState("");
  const [persistedProfilePicturePath, setPersistedProfilePicturePath] = useState("");
  const [pendingImageFile, setPendingImageFile] = useState(null);
  const [pendingImageUrl, setPendingImageUrl] = useState("");
  const [pendingImageDimensions, setPendingImageDimensions] = useState(null);
  const [imageZoom, setImageZoom] = useState(1);
  const [imageOffsetX, setImageOffsetX] = useState(0);
  const [imageOffsetY, setImageOffsetY] = useState(0);
  const [savedSnapshotKey, setSavedSnapshotKey] = useState("");
  // Visibility of the sociopolitical answers; seeded from the loaded profile
  // (opt-out model: defaults to public). Persisted with the rest of the profile.
  const [sociopoliticalPublic, setSociopoliticalPublic] = useState(true);
  const [formData, setFormData] = useState({
    profileName: "",
    email: "",
    country: "",
    state: "",
    city: "",
    dateOfBirth: "",
    ethnicity: "",
    maritalStatus: "",
    occupation: "",
    religiousAffiliation: "",
    politicalAffiliation: "",
    gender: "",
    sexualOrientation: "",
    story: "",
    interests: [],
    profilePicture: "",
  });

  const [sociopoliticalFilters, setSociopoliticalFilters] = useState(
    Object.entries(DEFAULT_SOCIOPOLITICAL_FILTERS).reduce((accumulator, [key, value]) => {
      accumulator[key] = { ...value };
      return accumulator;
    }, {})
  );

  const sociopoliticalQuestions = useMemo(
    () => [
      {
        id: "politicalSpectrum",
        label: "Political Spectrum",
        leftLabel: "Liberal",
        rightLabel: "Conservative",
      },
      {
        id: "immigration",
        label: "Immigration",
        leftLabel: "More Open Immigration",
        rightLabel: "Stricter Border Enforcement",
      },
      {
        id: "gunControl",
        label: "Gun Control",
        leftLabel: "Stricter Gun Laws",
        rightLabel: "Expanded Gun Rights",
      },
      {
        id: "abortion",
        label: "Abortion",
        leftLabel: "Broader Access",
        rightLabel: "More Restrictions",
      },
      {
        id: "climateChange",
        label: "Climate Change",
        leftLabel: "Urgent Climate Action",
        rightLabel: "Lower Climate Priority",
      },
      {
        id: "taxPolicy",
        label: "Tax Policy",
        leftLabel: "Higher Taxes & Services",
        rightLabel: "Lower Taxes & Smaller Government",
      },
      {
        id: "lawEnforcement",
        label: "Law Enforcement",
        leftLabel: "Reallocate Police Funding",
        rightLabel: "Expand Police Support",
      },
      {
        id: "votingIntegrity",
        label: "Voting Integrity",
        leftLabel: "Easier Ballot Access",
        rightLabel: "Stronger Voter ID Rules",
      },
      {
        id: "regulatoryPhilosophy",
        label: "Regulatory Philosophy",
        leftLabel: "More Federal Regulation",
        rightLabel: "Less Federal Regulation",
      },
      {
        id: "tradePolicy",
        label: "Trade Policy",
        leftLabel: "Free Trade Focus",
        rightLabel: "Tariff Protection Focus",
      },
      {
        id: "healthcare",
        label: "Healthcare",
        leftLabel: "Public Coverage Focus",
        rightLabel: "Private Coverage Focus",
      },
    ],
    []
  );

  const interestOptions = useMemo(
    () => [
      "Travel",
      "Cooking",
      "Sports",
      "Reading",
      "Music",
      "Art",
      "Technology",
      "Gaming",
      "Fitness",
      "Photography",
      "Writing",
      "Movies",
      "Fashion",
      "Volunteering",
      "Nature",
    ],
    []
  );

  const profileNameModeration = useMemo(
    () =>
      evaluateClientTextModeration({
        fieldType: "username",
        text: formData.profileName,
      }),
    [formData.profileName]
  );

  const storyModeration = useMemo(
    () =>
      evaluateClientTextModeration({
        fieldType: "profile_description",
        text: formData.story,
      }),
    [formData.story]
  );

  const currentSnapshotKey = useMemo(
    () =>
      JSON.stringify(
        buildProfileSnapshot(formData, sociopoliticalFilters, sociopoliticalPublic)
      ),
    [formData, sociopoliticalFilters, sociopoliticalPublic]
  );

  const hasUnsavedChanges = useMemo(() => {
    if (loading) return false;
    if (pendingImageFile) return true;
    if (!savedSnapshotKey) return false;
    return currentSnapshotKey !== savedSnapshotKey;
  }, [currentSnapshotKey, loading, pendingImageFile, savedSnapshotKey]);

  useEffect(() => {
    let cancelled = false;

    async function fetchUserData() {
      try {
        const currentUser = await getCurrentUser();
        const attributes = await fetchUserAttributes();

        if (cancelled) return;

        setCognitoId(currentUser.userId);
        setEmail(attributes?.email || "");
        const response = await client.queries.getUserProfile({
          cognitoId: currentUser.userId,
        });
        if (response?.errors?.length || !response?.data) {
          throw new Error("Failed to fetch user data");
        }

        const userData = response.data;
        if (cancelled) return;

        const nextFormData = {
          profileName: userData.profileName || "",
          email: attributes?.email || userData.email || "",
          country: userData.location?.country || "",
          state: userData.location?.state || "",
          city: userData.location?.city || "",
          dateOfBirth: userData.demographics?.dateOfBirth
            ? new Date(userData.demographics.dateOfBirth).toISOString().split("T")[0]
            : "",
          ethnicity: userData.demographics?.ethnicity || "",
          maritalStatus: userData.demographics?.maritalStatus || "",
          occupation: userData.preferences?.occupation || "",
          religiousAffiliation: userData.preferences?.religiousAffiliation || "",
          politicalAffiliation: userData.preferences?.politicalAffiliation || "",
          gender: userData.demographics?.gender || "",
          sexualOrientation: userData.demographics?.sexualOrientation || "",
          story: userData.story || "",
          interests: userData.interests || [],
          profilePicture: userData.profilePicture || "",
        };
        setFormData(nextFormData);
        setPersistedProfilePicturePath(userData.profilePicture || "");

        // Opt-out model: only an explicit stored `false` is private.
        const nextSociopoliticalPublic = userData.sociopoliticalPublic !== false;
        setSociopoliticalPublic(nextSociopoliticalPublic);

        const savedAnswers = userData.sociopoliticalAnswers || {};
        const nextSociopoliticalFilters = Object.keys(DEFAULT_SOCIOPOLITICAL_FILTERS).reduce(
          (accumulator, key) => {
            const rawValue = Number(savedAnswers[key]);
            const isOptedOut = rawValue === 0;
            const isValid = Number.isFinite(rawValue) && rawValue >= 1 && rawValue <= 7;
            accumulator[key] = {
              ...DEFAULT_SOCIOPOLITICAL_FILTERS[key],
              value: isValid ? rawValue : 4,
              irrelevant: isOptedOut,
            };
            return accumulator;
          },
          {}
        );
        setSociopoliticalFilters(nextSociopoliticalFilters);
        setSavedSnapshotKey(
          JSON.stringify(
            buildProfileSnapshot(
              nextFormData,
              nextSociopoliticalFilters,
              nextSociopoliticalPublic
            )
          )
        );

        if (userData.profilePicture) {
          try {
            const urlResult = await getUrl({
              path: userData.profilePicture,
            });
            setProfilePictureUrl(urlResult.url.toString());
          } catch (error) {
            console.error("Error fetching profile picture URL:", error);
          }
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching user data:", error);
        setError("Failed to load profile data");
        setLoading(false);
      }
    }

    fetchUserData();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((previousFormData) => ({ ...previousFormData, [name]: value }));
  };

  const handleImageUpload = async (event) => {
    const fileInput = event.target;
    const file = fileInput.files?.[0];
    // Reset the input value up-front so re-picking the same file (after a
    // validation error or a cancel) reliably triggers onChange. Browsers
    // dedupe identical selections otherwise and the click looks frozen.
    const resetInput = () => {
      if (fileInput) fileInput.value = "";
    };
    if (!file) {
      resetInput();
      return;
    }

    const validationError = validateProfileImageFile(file);
    if (validationError) {
      setError(validationError);
      resetInput();
      return;
    }

    setError("");
    const nextPendingImageUrl = URL.createObjectURL(file);

    let dimensions = null;
    try {
      dimensions = await loadImageDimensionsFromUrl(nextPendingImageUrl);
    } catch {
      revokeObjectUrl(nextPendingImageUrl);
      setError("Failed to load image. Please try a different file.");
      resetInput();
      return;
    }

    revokeObjectUrl(pendingImageUrl);
    setPendingImageFile(file);
    setPendingImageUrl(nextPendingImageUrl);
    setPendingImageDimensions(dimensions);
    setImageZoom(1);
    setImageOffsetX(0);
    setImageOffsetY(0);
    resetInput();
  };

  const handleCancelImageAdjustments = () => {
    revokeObjectUrl(pendingImageUrl);
    setPendingImageFile(null);
    setPendingImageUrl("");
    setPendingImageDimensions(null);
    setImageZoom(1);
    setImageOffsetX(0);
    setImageOffsetY(0);
  };

  const handleApplyImageAdjustments = async () => {
    if (!pendingImageFile) return;

    setUploading(true);
    setError("");

    let uploadedFilePath = "";
    let persistedAppliedPicture = false;
    try {
      if (!cognitoId) throw new Error("User not authenticated");

      const previousFormPicturePath = formData.profilePicture;
      const previousPersistedPicturePath = persistedProfilePicturePath;
      const clampedOffsets = clampImageOffsets(
        pendingImageDimensions,
        imageOffsetX,
        imageOffsetY,
        imageZoom
      );
      const processedBlob = await renderCroppedProfileImage({
        file: pendingImageFile,
        zoom: imageZoom,
        offsetX: clampedOffsets.x,
        offsetY: clampedOffsets.y,
      });
      const processedFile = new File([processedBlob], "profile-avatar.jpg", {
        type: PROFILE_IMAGE_OUTPUT_TYPE,
      });

      const profilePictureOwnerId = await resolveProfilePictureOwnerId({
        cognitoId,
        fetchAuthSessionImpl: fetchAuthSession,
      });
      const fileName = buildProfilePicturePath(
        cognitoId,
        PROFILE_IMAGE_OUTPUT_EXTENSION,
        profilePictureOwnerId
      );
      uploadedFilePath = fileName;
      await uploadData({
        path: fileName,
        data: processedFile,
        options: {
          contentType: PROFILE_IMAGE_OUTPUT_TYPE,
        },
      }).result;

      const persistResponse = await client.mutations.completeUserProfile({
        cognitoId,
        profile: {
          profilePicture: fileName,
        },
      });
      if (persistResponse?.errors?.length) {
        throw new Error(
          persistResponse.errors.map((entry) => entry.message).join(", ")
        );
      }
      persistedAppliedPicture = true;

      setFormData((previousFormData) => ({
        ...previousFormData,
        profilePicture: fileName,
      }));
      setPersistedProfilePicturePath(fileName);
      try {
        const urlResult = await getUrl({
          path: fileName,
        });
        setProfilePictureUrl(urlResult.url.toString());
      } catch (error) {
        console.error("Error fetching uploaded profile picture URL:", error);
        setProfilePictureUrl("");
      }

      if (
        previousFormPicturePath &&
        previousFormPicturePath !== fileName &&
        previousFormPicturePath !== previousPersistedPicturePath
      ) {
        try {
          await remove({ path: previousFormPicturePath });
        } catch (error) {
          console.error("Error deleting temporary profile picture:", error);
        }
      }

      if (
        previousPersistedPicturePath &&
        previousPersistedPicturePath !== fileName
      ) {
        try {
          await remove({ path: previousPersistedPicturePath });
        } catch (error) {
          console.error("Error deleting previous profile picture:", error);
        }
      }

      Hub.dispatch("profile", {
        event: "profile-updated",
        data: { cognitoId },
      });

      revokeObjectUrl(pendingImageUrl);
      setPendingImageFile(null);
      setPendingImageUrl("");
      setPendingImageDimensions(null);
      setImageZoom(1);
      setImageOffsetX(0);
      setImageOffsetY(0);
    } catch (error) {
      console.error("Error uploading image:", error);
      setError("Failed to upload image. Please try again.");
      if (uploadedFilePath && !persistedAppliedPicture) {
        try {
          await remove({ path: uploadedFilePath });
        } catch (cleanupError) {
          console.error(
            "Error cleaning up failed profile image upload:",
            cleanupError
          );
        }
      }
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    return () => {
      revokeObjectUrl(pendingImageUrl);
    };
  }, [pendingImageUrl]);

  // Clear the success-banner timer on unmount so it cannot fire after the
  // component has been torn down and cause a no-op state update.
  useEffect(() => {
    return () => {
      clearTimeout(successTimerRef.current);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    // If the form's stored picture path was cleared, drop the displayed URL
    // so the fallback initial renders. But on transient fetch errors, keep
    // the existing URL — wiping it on a timeout caused the photo to "appear
    // then vanish" right after a successful apply.
    if (!formData.profilePicture) {
      setProfilePictureUrl("");
      return;
    }

    const fetchProfilePictureUrl = async () => {
      try {
        const urlResult = await getUrl({
          path: formData.profilePicture,
        });
        if (!cancelled) {
          setProfilePictureUrl(urlResult.url.toString());
        }
      } catch (error) {
        console.error("Error fetching profile picture URL:", error);
      }
    };

    fetchProfilePictureUrl();
    return () => {
      cancelled = true;
    };
  }, [formData.profilePicture]);

  const handleInterestToggle = (interest) => {
    setFormData((previousFormData) => ({
      ...previousFormData,
      interests: previousFormData.interests.includes(interest)
        ? previousFormData.interests.filter((entry) => entry !== interest)
        : [...previousFormData.interests, interest],
    }));
  };

  const handleSociopoliticalChange = (questionId, value) => {
    setSociopoliticalFilters((previousFilters) => ({
      ...previousFilters,
      [questionId]: {
        ...previousFilters[questionId],
        value: parseInt(value, 10),
      },
    }));
  };

  const handleIrrelevantToggle = (questionId) => {
    setSociopoliticalFilters((previousFilters) => ({
      ...previousFilters,
      [questionId]: {
        ...previousFilters[questionId],
        irrelevant: !previousFilters[questionId].irrelevant,
        value: !previousFilters[questionId].irrelevant
          ? null
          : previousFilters[questionId].value,
      },
    }));
  };

  const saveProfile = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setError("");
    setSuccess(false);

    try {
      if (pendingImageFile) {
        throw new Error("Please apply your profile photo adjustments before saving.");
      }

      const sexualOrientation = (formData.sexualOrientation || "").trim();
      if (!sexualOrientation) {
        throw new Error("Please select a sexual orientation");
      }
      const ageValidationError = validateMinimumProfileAge(formData.dateOfBirth);
      if (ageValidationError) {
        throw new Error(ageValidationError);
      }

      const nextEmail = String(formData.email || "").trim();
      if (!nextEmail) {
        throw new Error("Please enter an email address.");
      }
      if (profileNameModeration.blocked) {
        throw new Error(profileNameModeration.feedback);
      }
      if (storyModeration.blocked) {
        throw new Error(storyModeration.feedback);
      }

      const answeredCount = Object.values(sociopoliticalFilters).filter(
        (question) => !question.irrelevant
      ).length;
      if (answeredCount < 8) {
        throw new Error(
          `Please answer at least 8 sociopolitical questions. You currently have ${answeredCount} answered.`
        );
      }

      if (!cognitoId) throw new Error("User not authenticated");
      const trimmedProfileName = String(formData.profileName || "").trim();
      if (!trimmedProfileName) {
        throw new Error("Profile name is required.");
      }

      // Uniqueness check. The server-side query excludes the current user's
      // own row, so submitting the same alias unchanged returns false.
      // Renaming to someone else's alias surfaces here before we attempt the
      // mutation, which keeps the error message specific to the alias field.
      const conflictResponse = await client.queries.checkProfileConflicts({
        cognitoId,
        profileName: trimmedProfileName,
      });

      if (conflictResponse?.errors?.length) {
        throw new Error(
          conflictResponse.errors.map((entry) => entry.message).join(", ")
        );
      }

      const conflictData =
        conflictResponse?.data?.checkProfileConflicts ||
        conflictResponse?.data ||
        {};

      if (conflictData?.profileNameTaken) {
        // Mirror the existing duplicate-name handler in the catch block:
        // alert is the right channel because the user has to type a new
        // value before the save can succeed, not just dismiss an inline
        // error. Returning here also avoids re-triggering setSaving(false)
        // in catch — the finally block still runs.
        const friendlyMessage =
          "That alias is already taken. Please choose a different one.";
        window.alert(friendlyMessage);
        setError(friendlyMessage);
        return { success: false, error: friendlyMessage };
      }

      const sociopoliticalAnswers =
        normalizeSociopoliticalAnswers(sociopoliticalFilters);

      const response = await client.mutations.completeUserProfile({
        cognitoId,
        profile: {
          profileName: formData.profileName,
          location: {
            country: formData.country,
            state: formData.state,
            city: formData.city,
          },
          demographics: {
            dateOfBirth: formData.dateOfBirth
              ? new Date(formData.dateOfBirth).toISOString()
              : null,
            ethnicity: formData.ethnicity,
            maritalStatus: formData.maritalStatus,
            gender: formData.gender,
            sexualOrientation,
          },
          preferences: {
            occupation: formData.occupation,
            religiousAffiliation: formData.religiousAffiliation,
            politicalAffiliation: formData.politicalAffiliation,
          },
          story: formData.story,
          interests: formData.interests,
          profilePicture: formData.profilePicture,
          sociopoliticalAnswers,
          sociopoliticalPublic,
        },
      });

      if (response?.errors?.length) {
        throw new Error(response.errors.map((entry) => entry.message).join(", "));
      }

      await updateUserAttributes({
        userAttributes: {
          name: formData.profileName.trim(),
          email: nextEmail,
        },
      });
      setEmail(nextEmail);

      if (
        persistedProfilePicturePath &&
        formData.profilePicture &&
        persistedProfilePicturePath !== formData.profilePicture
      ) {
        try {
          await remove({ path: persistedProfilePicturePath });
        } catch (error) {
          console.error("Error deleting previous profile picture:", error);
        }
      }
      setPersistedProfilePicturePath(formData.profilePicture || "");
      setSavedSnapshotKey(
        JSON.stringify(
          buildProfileSnapshot(
            {
              ...formData,
              sexualOrientation,
            },
            sociopoliticalFilters,
            sociopoliticalPublic
          )
        )
      );

      setSuccess(true);
      clearTimeout(successTimerRef.current);
      successTimerRef.current = setTimeout(() => setSuccess(false), 3000);

      Hub.dispatch("profile", {
        event: "profile-updated",
        data: { cognitoId },
      });

      return { success: true };
    } catch (error) {
      const message = error?.message || "Failed to update profile";
      const moderationFeedback = extractModerationFeedback(error);
      if (moderationFeedback.isModerationError) {
        setError(moderationFeedback.feedback);
        return { success: false, error: moderationFeedback.feedback };
      }

      if (isProfileNameTakenError(message)) {
        const friendlyMessage =
          "That username is already taken. Please choose a different profile name.";
        window.alert(friendlyMessage);
        setError(friendlyMessage);
        return { success: false, error: friendlyMessage };
      }

      setError(message);
      return { success: false, error: message };
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await saveProfile();
  };

  return {
    loading,
    saving,
    error,
    success,
    uploading,
    emailInitial: (email?.[0] || "?").toUpperCase(),
    formData,
    profilePictureUrl,
    hasPendingImage: Boolean(pendingImageFile),
    pendingImageUrl,
    pendingImageDimensions,
    imageZoom,
    imageOffsetX,
    imageOffsetY,
    storyModeration,
    sociopoliticalFilters,
    sociopoliticalPublic,
    setSociopoliticalPublic,
    hasUnsavedChanges,
    interestOptions,
    sociopoliticalQuestions,
    handleChange,
    handleImageUpload,
    setImageZoom,
    setImageOffsetX,
    setImageOffsetY,
    handleApplyImageAdjustments,
    handleCancelImageAdjustments,
    handleInterestToggle,
    handleSociopoliticalChange,
    handleIrrelevantToggle,
    saveProfile,
    handleSubmit,
  };
}
